import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';

interface Props {
  visible: boolean;
  questions: any[];
  onClose: () => void;
  onSubmit: (questionResponses: any[]) => Promise<void>;
}

interface EditableQuestion {
  questionCode: string;
  questionText: string;
  responseType: string;
  answerYesNo: boolean | null;
  answerText: string;
  isActive: boolean;
}

const formatQuestionText = (text: string) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const normalizeQuestions = (questions: any[]): EditableQuestion[] => {
  return (questions || [])
    .filter((q: any) => (q.AnswerYesNo ?? q.answerYesNo) !== null || (q.AnswerText ?? q.answerText))
    .map((q: any) => ({
      questionCode: q.QuestionCode || q.questionCode || '',
      questionText: formatQuestionText(q.QuestionText || q.questionText || ''),
      responseType: q.ResponseType || q.responseType || 'Text',
      answerYesNo: q.AnswerYesNo ?? q.answerYesNo ?? null,
      answerText: q.AnswerText || q.answerText || '',
      isActive: q.IsActive ?? q.isActive ?? true,
    }));
};

export function EditAdditionalInfoModal({ visible, questions, onClose, onSubmit }: Props) {
  const [items, setItems] = useState<EditableQuestion[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible) {
      setItems(normalizeQuestions(questions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleYesNoChange = (code: string, value: boolean) => {
    setItems(prev => prev.map(q => (q.questionCode === code ? { ...q, answerYesNo: value } : q)));
  };

  const handleTextChange = (code: string, text: string) => {
    setItems(prev => prev.map(q => (q.questionCode === code ? { ...q, answerText: text } : q)));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const questionResponses = items.map(q => ({
        questionCode: q.questionCode,
        answerYesNo: q.responseType === 'YesNo' ? q.answerYesNo : null,
        answerText: q.answerText || null,
        isActive: q.isActive,
      }));
      await onSubmit(questionResponses);
    } catch (err) {
      console.error('EditAdditionalInfoModal save failed:', err);
      Alert.alert('Error', 'Failed to update additional information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>Edit Additional Information</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn} disabled={saving}>
              <FeatherIcon name="x" size={20} color="#64748B" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {items.length === 0 ? (
              <Text style={styles.emptyText}>No additional information available.</Text>
            ) : (
              items.map((q, idx) => (
                <View key={q.questionCode || idx} style={styles.questionGroup}>
                  <Text style={styles.questionLabel}>{q.questionText}</Text>
                  {q.responseType === 'YesNo' ? (
                    <>
                      <View style={styles.chipRow}>
                        <TouchableOpacity
                          style={[styles.chip, q.answerYesNo === true && styles.chipSelected]}
                          onPress={() => handleYesNoChange(q.questionCode, true)}
                        >
                          <Text style={[styles.chipText, q.answerYesNo === true && styles.chipTextSelected]}>Yes</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.chip, q.answerYesNo === false && styles.chipSelected]}
                          onPress={() => handleYesNoChange(q.questionCode, false)}
                        >
                          <Text style={[styles.chipText, q.answerYesNo === false && styles.chipTextSelected]}>No</Text>
                        </TouchableOpacity>
                      </View>
                      {q.answerYesNo === true && (
                        <TextInput
                          style={[styles.input, styles.detailsInput]}
                          value={q.answerText}
                          onChangeText={text => handleTextChange(q.questionCode, text)}
                          multiline
                        />
                      )}
                    </>
                  ) : (
                    <TextInput
                      style={styles.input}
                      value={q.answerText}
                      onChangeText={text => handleTextChange(q.questionCode, text)}
                    />
                  )}
                </View>
              ))
            )}
          </ScrollView>

          <View style={styles.actions}>
            <TouchableOpacity style={styles.cancelBtn} onPress={onClose} disabled={saving}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color="#FFF" size="small" /> : <Text style={styles.saveText}>Save</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '100%',
    maxWidth: 440,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  title: {
    fontSize: 17,
    fontWeight: '800',
    color: '#1E293B',
  },
  closeBtn: {
    padding: 4,
  },
  body: {
    maxHeight: 420,
  },
  bodyContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  emptyText: {
    fontSize: 14,
    color: '#94A3B8',
    fontStyle: 'italic',
  },
  questionGroup: {
    marginBottom: 18,
  },
  questionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#64748B',
    marginBottom: 8,
    textTransform: 'capitalize',
    lineHeight: 18,
  },
  chipRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
  } as any,
  chip: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  chipSelected: {
    backgroundColor: '#1E40AF',
    borderColor: '#1E40AF',
  },
  chipText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  chipTextSelected: {
    color: '#FFFFFF',
  },
  input: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: '#1E293B',
    backgroundColor: '#F8FAFC',
  },
  detailsInput: {
    minHeight: 70,
    textAlignVertical: 'top',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  cancelBtn: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: '#F1F5F9',
  },
  cancelText: {
    color: '#475569',
    fontWeight: '700',
    fontSize: 14,
  },
  saveBtn: {
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 10,
    backgroundColor: '#1E40AF',
    minWidth: 90,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
});
